import { useState, useEffect, useCallback } from 'react';
// No imports needed - removed minimum width constraints
import { useSidebar } from '@/components/ui/sidebar';

interface CalendarSettings {
  hidePastDays: boolean;
  expandAllDays: boolean;
  hourCellWidthPx: number; // Legacy - CSS Grid handles width automatically
  show24Hours: boolean;
  selectedMonth: string; // ISO string to store the selected month
}

const STORAGE_KEY_PREFIX = 'calendar_settings_';

export const useCalendarSettings = (businessId?: string | null) => {
  // Get real sidebar state
  const { state: sidebarState } = useSidebar();
  const areSidePanelsCollapsed = sidebarState === 'collapsed';
  
  // Create a unique storage key for each business  
  const storageKey = businessId ? `${STORAGE_KEY_PREFIX}${businessId}` : STORAGE_KEY_PREFIX + 'default';

  // Use default settings without localStorage persistence
  const getDefaults = (): CalendarSettings => ({
    hidePastDays: true,
    expandAllDays: true,
    hourCellWidthPx: 0, // CSS Grid handles width naturally
    show24Hours: false,
    selectedMonth: new Date().toISOString(), // Default to current month
  });

  const [settings, setSettings] = useState<CalendarSettings>(getDefaults);

  // No localStorage persistence - settings reset on page reload

  // Update functions for each setting
  const setHidePastDays = useCallback((value: boolean) => {
    setSettings(prev => ({ ...prev, hidePastDays: value }));
  }, []);

  const setExpandAllDays = useCallback((value: boolean) => {
    setSettings(prev => ({ ...prev, expandAllDays: value }));
  }, []);

  const setHourCellWidthPx = useCallback((value: number | ((prev: number) => number)) => {
    // Legacy function - CSS Grid now handles width automatically
    // Kept for backward compatibility but has no effect on actual layout
    setSettings(prev => ({
      ...prev,
      hourCellWidthPx: typeof value === 'function' ? value(prev.hourCellWidthPx) : value,
    }));
  }, []);

  const setShow24Hours = useCallback((value: boolean) => {
    setSettings(prev => ({ ...prev, show24Hours: value }));
  }, []);


  const setSelectedMonth = useCallback((value: Date) => {
    // Validate the input date
    if (isNaN(value.getTime())) {
      return;
    }
    setSettings(prev => ({ ...prev, selectedMonth: value.toISOString() }));
  }, []);


  // Sidebar state is now managed by the SidebarProvider, not by calendar settings
  // This function is kept for compatibility but does nothing
  const setAreSidePanelsCollapsed = useCallback((value: boolean) => {
    // Deprecated - sidebar state is managed by SidebarProvider
  }, []);

  // Reset settings to defaults
  const resetSettings = useCallback(() => {
    const defaults: CalendarSettings = {
      hidePastDays: true,
      expandAllDays: true,
      hourCellWidthPx: 0, // CSS Grid handles width naturally
      show24Hours: false,
      selectedMonth: new Date().toISOString(), // Default to current month
    };
    setSettings(defaults);
  }, []);

  // Helper function to get selectedMonth as Date object with error handling
  const getSelectedMonth = useCallback(() => {
    try {
      const date = new Date(settings.selectedMonth);
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return new Date();
      }
      return date;
    } catch (error) {
      return new Date();
    }
  }, [settings.selectedMonth]);


  return {
    hidePastDays: settings.hidePastDays,
    expandAllDays: settings.expandAllDays,
    hourCellWidthPx: settings.hourCellWidthPx,
    show24Hours: settings.show24Hours,
    selectedMonth: getSelectedMonth(), // Return as Date object
    areSidePanelsCollapsed, // Now comes from real sidebar state
    setHidePastDays,
    setExpandAllDays,
    setHourCellWidthPx,
    setShow24Hours,
    setSelectedMonth,
    setAreSidePanelsCollapsed, // Kept for compatibility but deprecated
    resetSettings,
  };
};